/**
 * The drawing search palette's index and matcher: pure functions over the drawing's
 * annotations, the other drawings the user can open and the sheet's click targets, so
 * the workspace can memoize the index on its inputs and rerun only the cheap match on
 * every keystroke.
 *
 * A tag is written with and without separators (`15XV-01101` on the sheet, `15XV01101`
 * as an SME types it), so both sides are compared with the separators dropped.
 */
import {
  instrumentRegions,
  type BoundingBox,
  type ClickTarget,
  type PidDocument,
  type PnidInstrument,
  type PnidWizardState,
} from '../../types';
import { retireUnderSavedRegions } from '../clickTargetGeometry';
import { nodeGeometryBbox } from '../elementSavePayloads';
import { navigationBoxOf } from '../navigationBoxOf';
import { equipmentTargetLabel, instrumentTargetLabel, nodeTargetLabel } from '../targetRailItems';

export type SearchHitKind = 'instrument' | 'equipment' | 'line' | 'drawing' | 'click_target';

export interface SearchHit {
  /** Unique across kinds, for a list key and an option id. */
  key: string;
  kind: SearchHitKind;
  /** The annotation's id, the document's id for a drawing, or the id of the first place a click target's text appears. */
  id: string;
  /** The tag, a drawing's number, or a click target's text. */
  primary: string;
  /** The name, a line's `source → dest`, a drawing's title, or how many places hold a click target's text, when the thing has one. */
  secondary?: string;
  /** Where the canvas centers on a pick; null for a drawing and for a thing with no geometry yet. */
  bbox: BoundingBox | null;
  /** Reading order on the sheet, top to bottom then left to right; boxless things last, drawings in list order. */
  order: number;
}

/** One searchable string, normalized once when the index is built. */
type SearchTerm = {
  normalized: string;
  /** Offsets in `normalized` where a separator-split word begins, ascending. */
  wordStarts: number[];
};

type IndexEntry = {
  hit: SearchHit;
  /** Null when the shown primary is a placeholder, such as an untagged equipment's 'Untitled'. */
  primaryTerm: SearchTerm | null;
  secondaryTerms: SearchTerm[];
};
type UnorderedEntry = Omit<IndexEntry, 'hit'> & { hit: Omit<SearchHit, 'order'> };

export type SearchIndex = IndexEntry[];

const SEPARATORS = /[\s\-_/.:,]+/;
const SEPARATORS_GLOBAL = /[\s\-_/.:,]+/g;

/** Upper-cased, with whitespace and `- _ / . : ,` dropped. Quotes stay, so `3"` still means inches. */
export function normalize(text: string): string {
  return text.toUpperCase().replace(SEPARATORS_GLOBAL, '');
}

function searchTerm(text: string | null | undefined): SearchTerm | null {
  const trimmed = text?.trim();
  if (!trimmed) return null;
  const wordStarts: number[] = [];
  let offset = 0;
  for (const word of trimmed.split(SEPARATORS)) {
    if (!word) continue;
    wordStarts.push(offset);
    offset += word.length;
  }
  return { normalized: normalize(trimmed), wordStarts };
}

function searchTerms(...texts: (string | null | undefined)[]): SearchTerm[] {
  return texts.map(searchTerm).filter((term): term is SearchTerm => term !== null);
}

/** A drawing's number, else its file name. */
function drawingPrimary(drawing: PidDocument): string {
  return drawing.pnid_no?.trim() || drawing.original_filename;
}

/** Top to bottom, then left to right. */
function compareReadingOrder(first: BoundingBox, second: BoundingBox): number {
  return first.y1 - second.y1 || first.x1 - second.x1;
}

/** A read shorter than this is a stray balloon mark or an alarm letter: `0`, `S`, `AL`. */
const MIN_CLICK_TARGET_CHARACTERS = 3;
/** A read with no letter and fewer digits is a table value or a pipe size: `150`, `3/4"`. A sheet reference such as `15-075` has more. */
const MIN_LETTERLESS_CLICK_TARGET_DIGITS = 5;

function canIdentifyAPlace(normalizedText: string): boolean {
  if (normalizedText.length < MIN_CLICK_TARGET_CHARACTERS) return false;
  return /\p{L}/u.test(normalizedText)
    || normalizedText.replace(/\D/g, '').length >= MIN_LETTERLESS_CLICK_TARGET_DIGITS;
}

/**
 * The sheet text worth searching, one entry per distinct text at its first place in reading order.
 *
 * DECISION(2026-11-02, Abhishek): Search lists a text click target only when no marked item names it and its text can identify a place on the sheet, and identical text takes one row.
 * Marked means the same text as an item's tag, line number or description anywhere on the sheet, or a centre in a tagged instrument's region, which catches a balloon's OCR fragments such as `1511` for `15II`. An untagged instrument's region stays searchable because the tag printed there is findable nowhere else, and an equipment box stays searchable because it holds other instruments' tags. A printed tag that search cannot find, or a row that repeats a marked item, flags a wrong outcome.
 */
function clickTargetEntries(
  clickTargets: ClickTarget[],
  instruments: PnidInstrument[],
  markedTexts: Set<string>,
): UnorderedEntry[] {
  const taggedRegions = instruments.filter((instrument) => instrument.tag_status === 'tagged').flatMap(instrumentRegions);
  const placesByText = new Map<string, (ClickTarget & { text: string })[]>();
  for (const clickTarget of retireUnderSavedRegions(clickTargets, taggedRegions)) {
    const { kind, text } = clickTarget;
    if (kind !== 'text' || !text) continue;
    const normalizedText = normalize(text);
    if (markedTexts.has(normalizedText) || !canIdentifyAPlace(normalizedText)) continue;
    const places = placesByText.get(normalizedText) ?? [];
    places.push({ ...clickTarget, text });
    placesByText.set(normalizedText, places);
  }
  return [...placesByText.values()].map((places) => {
    const firstPlace = places.reduce((first, place) => (compareReadingOrder(place.bbox, first.bbox) < 0 ? place : first));
    const primary = firstPlace.text.replace(/\s+/g, ' ').trim();
    return {
      hit: {
        key: `click_target:${firstPlace.id}`, kind: 'click_target', id: firstPlace.id, primary,
        secondary: places.length > 1 ? `Sheet text · ${places.length} places` : 'Sheet text',
        bbox: firstPlace.bbox,
      },
      primaryTerm: searchTerm(primary),
      secondaryTerms: [],
    };
  });
}

/**
 * Everything the palette can find: the drawing's instruments, equipment and lines,
 * every drawing in `drawingList` but the open one, and the sheet text in `clickTargets`
 * that no marked item names.
 */
export function buildSearchIndex(
  pnidState: Pick<PnidWizardState, 'instruments' | 'equipments' | 'nodes'> | null,
  drawingList?: { drawings: PidDocument[]; openDrawingId: string },
  clickTargets: ClickTarget[] = [],
): SearchIndex {
  const entries: UnorderedEntry[] = [];
  for (const instrument of pnidState?.instruments ?? []) {
    const label = instrumentTargetLabel(instrument);
    const description = instrument.description?.trim() || undefined;
    entries.push({
      hit: {
        key: `instrument:${instrument.id}`, kind: 'instrument', id: instrument.id,
        primary: label, secondary: description, bbox: navigationBoxOf('instrument', instrument),
      },
      primaryTerm: searchTerm(label),
      secondaryTerms: searchTerms(description),
    });
  }
  for (const equipment of pnidState?.equipments ?? []) {
    const description = equipment.description?.trim() || undefined;
    entries.push({
      hit: {
        key: `equipment:${equipment.id}`, kind: 'equipment', id: equipment.id,
        primary: equipmentTargetLabel(equipment), secondary: description,
        bbox: navigationBoxOf('equipment', equipment),
      },
      primaryTerm: searchTerm(equipment.equipment_no),
      secondaryTerms: searchTerms(description),
    });
  }
  for (const node of pnidState?.nodes ?? []) {
    const ends = node.source_ref && node.dest_ref ? `${node.source_ref} → ${node.dest_ref}` : undefined;
    const label = node.line_no?.trim() || nodeTargetLabel(node);
    entries.push({
      hit: { key: `line:${node.id}`, kind: 'line', id: node.id, primary: label, secondary: ends, bbox: nodeGeometryBbox(node) },
      primaryTerm: searchTerm(label),
      secondaryTerms: searchTerms(ends),
    });
  }
  const markedTexts = new Set(
    entries.flatMap(({ primaryTerm, secondaryTerms }) => [primaryTerm, ...secondaryTerms]).flatMap((term) => (term ? [term.normalized] : [])),
  );
  // A plant name tells two drawings apart only when the list holds more than one plant.
  const showsPlant = new Set(drawingList?.drawings.map((drawing) => drawing.plant)).size > 1;
  for (const drawing of drawingList?.drawings ?? []) {
    if (drawing.id === drawingList?.openDrawingId) continue;
    const primary = drawingPrimary(drawing);
    const secondary = [drawing.pnid_title?.trim(), showsPlant ? drawing.plant : null].filter(Boolean).join(' · ');
    entries.push({
      hit: { key: `drawing:${drawing.id}`, kind: 'drawing', id: drawing.id, primary, secondary: secondary || undefined, bbox: null },
      primaryTerm: searchTerm(primary),
      secondaryTerms: searchTerms(drawing.pnid_title, primary === drawing.original_filename ? null : drawing.original_filename),
    });
  }
  entries.push(...clickTargetEntries(clickTargets, pnidState?.instruments ?? [], markedTexts));

  const readingOrder = entries
    .map((entry, index) => ({ bbox: entry.hit.bbox, index }))
    .sort((first, second) => {
      if (!first.bbox || !second.bbox) return (first.bbox ? 0 : 1) - (second.bbox ? 0 : 1);
      return compareReadingOrder(first.bbox, second.bbox);
    });
  const orderOf = new Map(readingOrder.map(({ index }, order) => [index, order]));
  return entries.map((entry, index) => ({ ...entry, hit: { ...entry.hit, order: orderOf.get(index)! } }));
}

/** How a hit matches, best first. Only a match in the primary string reaches tiers 0-3. */
const MATCH_TIER = {
  exact: 0,
  primaryStart: 1,
  primaryWordStart: 2,
  primarySubstring: 3,
  secondaryOnly: 4,
} as const;
type MatchTier = (typeof MATCH_TIER)[keyof typeof MATCH_TIER];

/** The kind breaks a tie only after tier, position and length. */
const KIND_TIE_BREAK: Record<SearchHitKind, number> = { instrument: 0, equipment: 1, line: 2, drawing: 3, click_target: 4 };

type RankedHit = {
  hit: SearchHit;
  tier: MatchTier;
  /** Where the match starts, in the string the tier is about. */
  position: number;
  primaryLength: number;
};

/** The best tier and its earliest position in one primary string, or null on no match. */
function primaryMatch(term: SearchTerm, query: string): { tier: MatchTier; position: number } | null {
  const { normalized, wordStarts } = term;
  if (normalized === query) return { tier: MATCH_TIER.exact, position: 0 };
  if (normalized.startsWith(query)) return { tier: MATCH_TIER.primaryStart, position: 0 };
  const wordStart = wordStarts.find((offset) => offset > 0 && normalized.startsWith(query, offset));
  if (wordStart !== undefined) return { tier: MATCH_TIER.primaryWordStart, position: wordStart };
  const at = normalized.indexOf(query);
  return at === -1 ? null : { tier: MATCH_TIER.primarySubstring, position: at };
}

function rankEntry({ hit, primaryTerm, secondaryTerms }: IndexEntry, query: string): RankedHit | null {
  const primaryLength = primaryTerm?.normalized.length ?? normalize(hit.primary).length;
  const inPrimary = primaryTerm && primaryMatch(primaryTerm, query);
  if (inPrimary) return { hit, ...inPrimary, primaryLength };
  const secondaryPositions = secondaryTerms
    .map((term) => term.normalized.indexOf(query))
    .filter((at) => at !== -1);
  if (secondaryPositions.length === 0) return null;
  return { hit, tier: MATCH_TIER.secondaryOnly, position: Math.min(...secondaryPositions), primaryLength };
}

const isClickTargetHit = ({ hit }: RankedHit): number => (hit.kind === 'click_target' ? 1 : 0);

/**
 * Sheet text after everything else, so it fills the list and never displaces a marked
 * item. Then tier, then where the match starts, then the shorter primary (`15XV-01101`
 * before `15XV-01101-NOTE`), then the kind, then reading order.
 */
function compareRankedHits(first: RankedHit, second: RankedHit): number {
  return isClickTargetHit(first) - isClickTargetHit(second)
    || first.tier - second.tier
    || first.position - second.position
    || first.primaryLength - second.primaryLength
    || KIND_TIE_BREAK[first.hit.kind] - KIND_TIE_BREAK[second.hit.kind]
    || first.hit.order - second.hit.order;
}

/** The palette lists this many hits; a longer query narrows the rest. */
const SHOWN_HITS = 10;

/** The best matches for `query`, separators ignored, best first. */
export function searchHits(index: SearchIndex, query: string): SearchHit[] {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) return [];
  const ranked: RankedHit[] = [];
  for (const entry of index) {
    const rankedHit = rankEntry(entry, normalizedQuery);
    if (rankedHit) ranked.push(rankedHit);
  }
  ranked.sort(compareRankedHits);
  return ranked.slice(0, SHOWN_HITS).map(({ hit }) => hit);
}
